import { useState, useRef, useEffect, useCallback } from 'react';
import { SearchBoxCore, SessionToken } from '@mapbox/search-js-core';
import type { SearchBoxSuggestion } from '@mapbox/search-js-core';

export interface AddressSuggestion {
  formatted: string;
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  lat: string | null;
  long: string | null;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

export function useMapboxSearch() {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchBoxRef = useRef<SearchBoxCore | null>(null);
  const sessionTokenRef = useRef<SessionToken>(new SessionToken());
  const mountedRef = useRef(true);
  const searchVersionRef = useRef(0);

  // Lazily initialize SearchBoxCore
  const getSearchBox = useCallback(() => {
    if (!searchBoxRef.current) {
      searchBoxRef.current = new SearchBoxCore({
        accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
      });
    }
    return searchBoxRef.current;
  }, []);

  // Track mounted state and clean up debounce timer on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const parseSuggestions = useCallback(async (
    rawSuggestions: SearchBoxSuggestion[],
    searchBox: SearchBoxCore
  ): Promise<AddressSuggestion[]> => {
    const settled = await Promise.allSettled(
      rawSuggestions.map(async (suggestion) => {
        const retrieveResponse = await searchBox.retrieve(suggestion, {
          sessionToken: sessionTokenRef.current,
        });

        const feature = retrieveResponse.features[0];
        if (!feature) return null;

        const props = feature.properties;
        const context = props.context || {};
        const featureType = props.feature_type || '';

        // For place/city results, the name IS the city — not address_line1
        // For country results, the name IS the country
        // For address results, address_line1 is the street address
        let address_line1 = '';
        let city = context.place?.name || '';
        let state = context.region?.name || '';
        const country = context.country?.name || '';

        if (featureType === 'address' || featureType === 'street') {
          address_line1 = props.address || props.name || '';
        } else if (featureType === 'place' || featureType === 'locality' || featureType === 'neighborhood') {
          // City/town/locality — name is the city
          city = props.name || city;
        } else if (featureType === 'region' || featureType === 'district') {
          // State/region — name is the state
          state = props.name || state;
        } else if (featureType === 'country') {
          // Country only — other fields stay empty
        } else {
          // Fallback: put name in address_line1
          address_line1 = props.address || props.name || '';
        }

        // Build a readable formatted string
        const formatted = props.full_address
          || [props.name, city, state, country].filter(Boolean).join(', ')
          || props.name
          || '';

        return {
          formatted,
          address_line1,
          city,
          state,
          postal_code: context.postcode?.name || '',
          country,
          lat: props.coordinates?.latitude != null
            ? String(props.coordinates.latitude)
            : null,
          long: props.coordinates?.longitude != null
            ? String(props.coordinates.longitude)
            : null,
        } satisfies AddressSuggestion;
      })
    );

    return settled
      .filter((r): r is PromiseFulfilledResult<AddressSuggestion | null> => r.status === 'fulfilled')
      .map((r) => r.value)
      .filter((v): v is AddressSuggestion => v !== null);
  }, []);

  const search = useCallback(
    (query: string) => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Don't search for short queries
      if (query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const version = ++searchVersionRef.current;

      debounceTimerRef.current = setTimeout(async () => {
        try {
          const searchBox = getSearchBox();
          const response = await searchBox.suggest(query, {
            sessionToken: sessionTokenRef.current,
          });

          // Discard results if a newer search has been initiated
          if (version !== searchVersionRef.current) return;

          const parsed = await parseSuggestions(
            response.suggestions,
            searchBox
          );

          if (mountedRef.current && version === searchVersionRef.current) {
            setSuggestions(parsed);
          }
        } catch {
          // On error, return empty suggestions
          if (mountedRef.current && version === searchVersionRef.current) {
            setSuggestions([]);
          }
        } finally {
          if (mountedRef.current && version === searchVersionRef.current) {
            setIsSearching(false);
          }
        }
      }, DEBOUNCE_MS);
    },
    [getSearchBox, parseSuggestions]
  );

  const clear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setSuggestions([]);
    setIsSearching(false);
    // Create a new session token for the next search session
    sessionTokenRef.current = new SessionToken();
  }, []);

  return { suggestions, isSearching, search, clear };
}
